/**
 * Headboard Product Customizer
 * Handles fabric selection, color swatches, and price calculations for headboard products
 */

class HeadboardCustomizer {
  constructor() {
    this.fabricData = {};
    this.currentFabric = 'Plush';
    this.currentColor = '';
    this.basePrice = 0;
    this.addonPrice = 0; // Headboard size addon
    this.mattressAddonPrice = 0; // Mattress addon
    this.gasLiftAddonPrice = 0; // Gas lift storage addon
    this.drawersAddonPrice = 0; // Storage drawers addon
    this.currencyFormat = '£{{amount}}';

    this.init();
  }

  init() {
    console.log('Headboard Customizer initializing...');

    // Load fabric data from JSON
    this.loadFabricData();
    console.log('Fabric data loaded:', Object.keys(this.fabricData).length, 'fabrics');

    // Get base price from selected variant
    this.updateBasePrice();

    // Set up event listeners
    this.setupEventListeners();
    console.log('Event listeners set up');

    // Sync current fabric to whichever fabric radio is actually checked
    // (e.g. boucle beds default to Teddy, others default to Plush)
    const checkedFabric = document.querySelector('input[name="fabric-select"]:checked');
    if (checkedFabric && checkedFabric.value) {
      this.currentFabric = checkedFabric.value;
    }

    // Load initial color swatches for the selected fabric
    this.loadColorSwatches(this.currentFabric);
    console.log('Initial color swatches loaded for:', this.currentFabric);

    // Prime Klarna placement amount on initial load (no addons yet, so total == basePrice)
    this.updatePriceDisplay();

    console.log('Headboard Customizer initialized successfully!');
  }

  loadFabricData() {
    const fabricDataElement = document.getElementById('headboard-fabrics-data');
    if (fabricDataElement) {
      try {
        this.fabricData = JSON.parse(fabricDataElement.textContent);
      } catch (e) {
        console.error('Error parsing fabric data:', e);
      }
    }
  }

  setupEventListeners() {
    document.querySelectorAll('input[name="installation-select"]').forEach(input => {
      input.addEventListener('change', () => {
        this.updatePriceDisplay();
        const selected = document.getElementById('installation-yes');
        document.querySelectorAll('#headboard-product-form .shopify-payment-button').forEach(button => {
          button.hidden = !!(selected && selected.checked);
        });
      });
    });
    // Customize Headboard Toggle
    const customizeInputs = document.querySelectorAll('input[name="customize_headboard"]');
    customizeInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleCustomizeToggle(e));
    });

    // Fabric selector change
    const fabricInputs = document.querySelectorAll('input[name="fabric-select"]');
    fabricInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleFabricChange(e));
    });

    // Headboard size change
    const sizeInputs = document.querySelectorAll('input[name="properties[Headboard Size]"]');
    sizeInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleSizeChange(e));
    });

    // Variant change (size variant)
    const variantSelectors = document.querySelectorAll('.js-single-option-selector');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => this.handleVariantChange());
    });

    // Listen to the main variant select dropdown
    const section = document.querySelector('[data-section-type="product-template"]');
    const sectionId = section ? section.dataset.sectionId : null;
    if (sectionId) {
      const mainVariantSelect = document.querySelector('.js-product-select--' + sectionId);
      if (mainVariantSelect) {
        mainVariantSelect.addEventListener('change', () => this.handleVariantChange());
      }
    }

    // Listen to existing variant change events
    document.addEventListener('variantChange', () => {
      setTimeout(() => this.handleVariantChange(), 50);
    });

    // Watch for price element changes in the DOM
    this.observePriceChanges();

    // Listen for mattress addon updates (framebed template only)
    document.addEventListener('mattress-addon-updated', (e) => {
      console.log('Mattress addon selected:', e.detail);
      this.handleMattressAddonUpdate(e.detail);
    });

    // Listen for gas lift addon updates (framebed template only)
    document.addEventListener('gas-lift-addon-updated', (e) => {
      console.log('Gas lift addon selected:', e.detail);
      this.handleGasLiftAddonUpdate(e.detail);
    });

    // Listen for drawers addon updates (divanbeds template only)
    document.addEventListener('drawers-addon-updated', (e) => {
      console.log('Drawers addon selected:', e.detail);
      this.handleDrawersAddonUpdate(e.detail);
    });
  }

  handleCustomizeToggle(event) {
    const isYes = event.target.value === 'Yes';
    const statusLabel = document.getElementById('customize-status');
    const sizeSection = document.getElementById('headboard-size-section');

    // Update label
    if (statusLabel) statusLabel.textContent = event.target.value;

    // Show/Hide Size Section
    if (sizeSection) {
      if (isYes) {
        sizeSection.classList.remove('hidden-by-default');
        // If "Yes", user can now select. We keep whatever is selected or default to 54".
      } else {
        sizeSection.classList.add('hidden-by-default');
        // If "No", reset to 54" (no cost)
        this.resetToDefaultSize();
      }
    }
  }

  resetToDefaultSize() {
    // Select the 54" input
    const defaultInput = document.getElementById('headboard-size-54');
    if (defaultInput) {
      defaultInput.checked = true;
      // Trigger change event logic manually since setting checked doesn't trigger it
      this.addonPrice = 0;

      // Update hidden field
      const priceAddonProperty = document.getElementById('price-addon-property');
      if (priceAddonProperty) priceAddonProperty.value = 0;

      // Update label
      const sizeLabel = document.getElementById('selected-headboard-size');
      if (sizeLabel) sizeLabel.textContent = '54"';

      this.updatePriceDisplay();
    }
  }

  handleFabricChange(event) {
    this.currentFabric = event.target.value;

    // Update fabric label
    const fabricLabel = document.getElementById('selected-fabric');
    if (fabricLabel) {
      fabricLabel.textContent = this.currentFabric;
    }

    // Update hidden field
    const fabricProperty = document.getElementById('fabric-property');
    if (fabricProperty) {
      fabricProperty.value = this.currentFabric;
    }

    // Reset color selection
    this.currentColor = '';
    const colorProperty = document.getElementById('color-property');
    if (colorProperty) {
      colorProperty.value = '';
    }

    const colorLabel = document.getElementById('selected-color');
    if (colorLabel) {
      colorLabel.textContent = 'Select a color';
    }

    // Load new color swatches
    this.loadColorSwatches(this.currentFabric);
  }

  loadColorSwatches(fabric) {
    const grid = document.getElementById('color-swatch-grid');
    if (!grid) return;

    // Clear existing swatches
    grid.innerHTML = '';

    // Get colors for this fabric
    const fabricColors = this.fabricData[fabric];
    if (!fabricColors || !fabricColors.colors) {
      console.warn('No colors found for fabric:', fabric);
      return;
    }

    // Create swatch for each color
    fabricColors.colors.forEach((color, index) => {
      const swatchDiv = document.createElement('div');
      swatchDiv.className = 'color-swatch';
      swatchDiv.dataset.colorName = color.name;

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'color-select';
      input.value = color.name;
      input.id = `color-${fabric}-${index}`;
      input.addEventListener('change', (e) => this.handleColorChange(e));

      const label = document.createElement('label');
      label.htmlFor = input.id;
      label.title = color.name;

      const img = document.createElement('img');
      img.src = color.swatch;
      img.alt = color.name;
      img.loading = 'lazy';

      label.appendChild(img);
      swatchDiv.appendChild(input);
      swatchDiv.appendChild(label);
      grid.appendChild(swatchDiv);
    });
  }

  handleColorChange(event) {
    this.currentColor = event.target.value;

    // Update color label
    const colorLabel = document.getElementById('selected-color');
    if (colorLabel) {
      colorLabel.textContent = this.currentColor;
    }

    // Update hidden field
    const colorProperty = document.getElementById('color-property');
    if (colorProperty) {
      colorProperty.value = this.currentColor;
    }
  }

  handleSizeChange(event) {
    const priceInPennies = parseInt(event.target.dataset.price) || 0;
    this.addonPrice = priceInPennies;

    // Update hidden price addon field
    const priceAddonProperty = document.getElementById('price-addon-property');
    if (priceAddonProperty) {
      priceAddonProperty.value = priceInPennies;
    }

    // Update price display
    this.updatePriceDisplay();
  }

  handleVariantChange() {
    console.log('Variant changed, updating prices...');
    // Update base price when variant changes
    this.updateBasePrice();
    // Force immediate price display update
    this.updatePriceDisplay();
  }

  updateBasePrice() {
    // Method 1: Try to get from product JSON and selected variant ID
    const section = document.querySelector('[data-section-type="product-template"]');
    const sectionId = section ? section.dataset.sectionId : null;

    if (sectionId) {
      // Get the selected variant ID from the main select
      const variantSelect = document.querySelector('.js-product-select--' + sectionId);
      if (variantSelect && variantSelect.value) {
        const selectedVariantId = parseInt(variantSelect.value);

        // Try to get product JSON data
        const productJsonElement = document.getElementById('ProductJson-' + sectionId);
        if (productJsonElement) {
          try {
            const productData = JSON.parse(productJsonElement.textContent);
            const selectedVariant = productData.variants.find(v => v.id === selectedVariantId);
            if (selectedVariant && selectedVariant.price) {
              this.basePrice = selectedVariant.price;
              console.log('Base price from product JSON:', this.formatMoney(this.basePrice));
              return;
            }
          } catch (e) {
            console.warn('Error parsing product JSON:', e);
          }
        }

        // Fallback: Extract price from option text
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (selectedOption) {
          const priceText = selectedOption.textContent.split('-').pop().trim();
          this.basePrice = this.parsePriceToInt(priceText);
          console.log('Base price from variant select:', this.formatMoney(this.basePrice));
          return;
        }
      }
    }

    // Method 2: Try to get from price display element
    const priceElement = document.querySelector('.product-single__price, .product-price__price, [data-product-price]');
    if (priceElement) {
      const priceText = priceElement.textContent || priceElement.innerText;
      this.basePrice = this.parsePriceToInt(priceText);
      console.log('Base price from price element:', this.formatMoney(this.basePrice));
      return;
    }

    // Method 3: Use Shopify's variant data if available
    if (typeof ShopifyAPI !== 'undefined' && ShopifyAPI.variant) {
      this.basePrice = ShopifyAPI.variant.price;
      console.log('Base price from ShopifyAPI:', this.formatMoney(this.basePrice));
      return;
    }

    console.warn('Could not determine base price. Make sure a variant is selected.');
  }

  observePriceChanges() {
    // Watch for changes to the price display elements
    const priceElement = document.querySelector('.product-single__price, .product-price__price, [data-product-price]');
    if (!priceElement) return;

    // Create a MutationObserver to watch for price changes
    const observer = new MutationObserver((mutations) => {
      // Skip mutations caused by our own syncMainPriceBlock writes
      if (this._syncingMainPrice) return;
      console.log('Price element changed, updating base price...');
      this.updateBasePrice();
      this.updatePriceDisplay();
    });

    // Observe changes to the price element's text content
    observer.observe(priceElement, {
      childList: true,
      characterData: true,
      subtree: true
    });

    console.log('Price observer set up successfully');
  }

  parsePriceToInt(priceString) {
    // Remove currency symbols and convert to pence/cents
    const cleaned = priceString.replace(/[^0-9.]/g, '');
    const float = parseFloat(cleaned);
    return Math.round(float * 100); // Convert to pence
  }

  formatMoney(amountInPence) {
    const amount = (amountInPence / 100).toFixed(2);
    return this.currencyFormat.replace('{{amount}}', amount);
  }

  updatePriceDisplay() {
    const baseDisplay = document.getElementById('base-price-display');
    const addonDisplay = document.getElementById('addon-price-display');
    const addonSection = document.getElementById('addon-price-section');
    const totalDisplay = document.getElementById('total-price-display');

    if (baseDisplay) {
      baseDisplay.textContent = this.formatMoney(this.basePrice);
    }

    // Calculate total addon price (headboard size + mattress + gas lift + drawers)
    const installation = document.getElementById('installation-yes');
    const installationPrice = installation && installation.checked ? (parseInt(installation.dataset.price, 10) || 0) : 0;
    const totalAddonPrice = this.addonPrice + this.mattressAddonPrice + this.gasLiftAddonPrice + this.drawersAddonPrice + installationPrice;

    if (totalAddonPrice > 0) {
      if (addonDisplay) {
        // Build breakdown parts
        const parts = [];
        if (installationPrice > 0) parts.push('Professional Assembly: +' + this.formatMoney(installationPrice));
        if (this.addonPrice > 0) parts.push('Headboard: +' + this.formatMoney(this.addonPrice));
        if (this.mattressAddonPrice > 0) parts.push('Mattress: +' + this.formatMoney(this.mattressAddonPrice));
        if (this.gasLiftAddonPrice > 0) parts.push('Gas Lift: +' + this.formatMoney(this.gasLiftAddonPrice));
        if (this.drawersAddonPrice > 0) parts.push('Drawers: +' + this.formatMoney(this.drawersAddonPrice));

        if (parts.length > 1) {
          addonDisplay.textContent = '+' + this.formatMoney(totalAddonPrice) + ' (' + parts.join(', ') + ')';
        } else {
          addonDisplay.textContent = '+' + this.formatMoney(totalAddonPrice) + ' (' + parts[0] + ')';
        }
      }
      if (addonSection) {
        addonSection.style.display = 'flex';
      }
    } else {
      if (addonSection) {
        addonSection.style.display = 'none';
      }
    }

    const total = this.basePrice + totalAddonPrice;

    if (totalDisplay) {
      totalDisplay.textContent = this.formatMoney(total);
    }

    // Sync the main product price block so Klarna OSM (which scrapes
    // .js-product-price / its `content` attribute) sees the addon-inclusive total.
    this.syncMainPriceBlock(total);

    // Update Klarna On-Site Messaging with the new total price
    this.refreshKlarnaMessaging(total);
  }

  syncMainPriceBlock(totalInPence) {
    var mainPriceEls = document.querySelectorAll('.js-product-price');
    if (!mainPriceEls.length) return;
    var formatted = this.formatMoney(totalInPence);
    var contentValue = (totalInPence / 100).toFixed(2);
    this._syncingMainPrice = true;
    mainPriceEls.forEach(function (el) {
      el.textContent = formatted;
      el.setAttribute('content', contentValue);
    });
    // Release flag on next tick so the observer (which fires async) sees it set
    var self = this;
    setTimeout(function () { self._syncingMainPrice = false; }, 0);
  }

  refreshKlarnaMessaging(totalInPence) {
    try {
      var amountStr = totalInPence.toString();
      var placements = document.querySelectorAll('klarna-placement');

      // Clone-and-replace each placement so Klarna's custom-element script
      // re-mounts it and reads the fresh data-purchase-amount. Just calling
      // setAttribute + 'refresh-placements' is not enough — the Shopify
      // Klarna OSM app caches the rendered content from the first mount.
      placements.forEach(function (placement) {
        placement.setAttribute('data-purchase-amount', amountStr);
        var fresh = placement.cloneNode(true);
        fresh.setAttribute('data-purchase-amount', amountStr);
        if (placement.parentNode) {
          placement.parentNode.replaceChild(fresh, placement);
        }
      });

      // Belt-and-braces: tell Klarna to refresh placements
      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });

      // KOSMApp API (Shopify Klarna OSM app), if exposed
      if (window.KOSMApp && typeof window.KOSMApp.updatePurchaseAmount === 'function') {
        window.KOSMApp.updatePurchaseAmount(totalInPence);
      }

      console.log('Klarna refreshed with price:', totalInPence);
    } catch (e) {
      console.warn('Klarna messaging update failed:', e);
    }
  }

  handleMattressAddonUpdate(detail) {
    // Update mattress addon price
    this.mattressAddonPrice = parseInt(detail.price) || 0;
    console.log('Mattress addon price updated to:', this.formatMoney(this.mattressAddonPrice));

    // Update price display
    this.updatePriceDisplay();
  }

  handleGasLiftAddonUpdate(detail) {
    // Update gas lift addon price
    this.gasLiftAddonPrice = parseInt(detail.price) || 0;
    console.log('Gas lift addon price updated to:', this.formatMoney(this.gasLiftAddonPrice));

    // Update price display
    this.updatePriceDisplay();
  }

  handleDrawersAddonUpdate(detail) {
    // Update drawers addon price
    this.drawersAddonPrice = parseInt(detail.price) || 0;
    console.log('Drawers addon price updated to:', this.formatMoney(this.drawersAddonPrice));

    // Update price display
    this.updatePriceDisplay();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('headboard-product-form')) {
      window.headboardCustomizer = new HeadboardCustomizer();
    }
  });
} else {
  if (document.getElementById('headboard-product-form')) {
    window.headboardCustomizer = new HeadboardCustomizer();
  }
}
